/**
 * Export menu — emits a CSV file and an XLSX file from a list of rows.
 *
 * Inputs:
 *   filename   base file name (no extension)
 *   columns    [{ key, label, format?(row) }]
 *   rows       data rows; export respects whatever filtering the caller
 *              has already applied
 *
 * Outputs:
 *   .csv  — UTF-8 BOM-prefixed so Excel double-click renders Indian
 *           rupee + accented characters correctly
 *   .xlsx — proper Office Open XML via the `xlsx` library
 */
import React from 'react';
import * as XLSX from 'xlsx';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Download, FileText } from 'lucide-react';

const _escapeCsv = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
};

const _buildMatrix = (columns, rows) => {
  const matrix = [columns.map((c) => c.label)];
  for (const r of rows) {
    matrix.push(columns.map((c) => (c.format ? c.format(r) : r[c.key])));
  }
  return matrix;
};

const _downloadCsv = (filename, matrix) => {
  const lines = matrix.map((row) => row.map(_escapeCsv).join(','));
  // UTF-8 BOM so Excel renders ₹ / accented chars on Windows.
  const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const _downloadXlsx = (filename, matrix) => {
  const ws = XLSX.utils.aoa_to_sheet(matrix);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

const ExportButton = ({ filename, columns, rows, fetchRows, testid = 'export-btn' }) => {
  const _rows = async () => (fetchRows ? await fetchRows() : rows);
  const exportCsv = async () => _downloadCsv(filename, _buildMatrix(columns, await _rows()));
  const exportXlsx = async () => _downloadXlsx(filename, _buildMatrix(columns, await _rows()));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" data-testid={testid} className="gap-1.5">
          <Download className="h-3.5 w-3.5" />
          Export
          <span className="text-[10px] text-slate-400">({rows.length})</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[140px]">
        <DropdownMenuItem onClick={exportCsv} data-testid={`${testid}-csv`}>
          <FileText className="h-3.5 w-3.5 mr-2" /> Download CSV
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportXlsx} data-testid={`${testid}-xlsx`}>
          <FileText className="h-3.5 w-3.5 mr-2" /> Download Excel
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportButton;
